/* eslint-disable no-unused-vars, max-len */
const { feWp } = window;
const { addAction } = feWp.hooks;

const configureSwiper = ( settings, swiper ) => {
  Object.assign( swiper.params, {
    threshold: 2,
    preventClicks: false,
    preventClicksPropagation: false
  } );
  if ( settings.loop ) {
    Object.assign( swiper.params, {
      supports4k: true,
      longSwipesRatio: 0,
      threshold: 0,
      touchRatio: 0.1
    } );
    swiper.update();
  }
};

const stopAutoPlay = ( sliderObj ) => sliderObj.autoplay.stop();
const startAutoPlay = ( sliderObj ) => sliderObj.autoplay.start();

const setAnimationDuration = ( sliderObj, duration ) => {
  sliderObj.el.querySelectorAll( '.line-animator' )?.forEach( line => {
    line.style.animationDuration = `${duration}ms`;
  } );
};

// Function to create single sliding background inside swiper-wrapper
const createSlidingBackground = ( navSwiper ) => {
  const swiperWrapper = navSwiper.el.querySelector( '.swiper-wrapper' );
  
  if ( swiperWrapper && !swiperWrapper.querySelector( '.slide-transition-bg' ) ) {
    const slidingBg = document.createElement( 'div' );
    slidingBg.className = 'slide-transition-bg';
    swiperWrapper.appendChild( slidingBg );
    
    // Initialize position under first active slide
    setTimeout( () => {
      moveSlidingBackground( navSwiper );
    }, 0 );
  }
};

// Function to move sliding background to active slide
const moveSlidingBackground = ( navSwiper, targetIndex = null ) => {
  const swiperWrapper = navSwiper.el.querySelector( '.swiper-wrapper' );
  const slidingBg = swiperWrapper?.querySelector( '.slide-transition-bg' );
  
  // Use targetIndex if provided, otherwise use activeIndex
  const slideIndex = targetIndex !== null ? targetIndex : navSwiper.activeIndex;
  const activeSlide = navSwiper.slides[slideIndex];
  
  if ( slidingBg && activeSlide ) {
    // Calculate position based on slide index, slide width, and margin
    const slideWidth = activeSlide.offsetWidth;
    const marginRight = 14; // Each slide has margin-right: 14px
    const left = slideIndex * ( slideWidth + marginRight );
    
    // Move the background using CSS transform
    slidingBg.style.transform = `translateX(${left}px)`;
    slidingBg.style.width = `${slideWidth}px`;
  }
};

const handleNavSlideColorTransition = ( navSwiper, direction, mainSwiper ) => {
  // Move sliding background to match the main swiper's active slide
  moveSlidingBackground( navSwiper, mainSwiper.activeIndex );
};

// Function to handle slide image transitions
const handleSlideImageTransition = ( swiper, direction ) => {
  const slides = swiper.slides;
  // Reset all slide images to initial state
  slides.forEach( slide => {
    const image = slide.querySelector( 'img' );
    if ( image ) {
      image.style.transition = 'none';
      image.style.transform = 'translateX(0)';
      // Force reflow
      image.offsetHeight; //NOSONAR
    }
  } );

  // Get the active slide and its image
  const activeSlide = slides[swiper.activeIndex];
  const activeImage = activeSlide?.querySelector( '.e-div--media-wrapper img' );
  
  if ( activeImage ) {
    // Set initial position based on direction
    const translateX = direction === 'next' ? '100%' : '-100%';
    activeImage.style.transition = 'none';
    activeImage.style.transform = `translateX(${translateX})`;
    
    // Force reflow
    activeImage.offsetHeight; //NOSONAR
    
    // Animate to final position
    activeImage.style.transition = 'transform 0.6s cubic-bezier(0.46, 0.03, 0.59, 1.35)';
    activeImage.style.transform = 'translateX(0)';
  }
};

const handleInteraction = ( swiper, navSwiper, el ) => {
  setAnimationDuration( navSwiper, 0 );
  stopAutoPlay( swiper );
  el.parentNode.classList.add( 'autoplay-stop' );
};

const handleDotsInteraction = ( swiper, navSwiper, el, dots ) => {
  dots.forEach( dot => {
    dot.addEventListener( 'click', () => {
      handleInteraction( swiper, navSwiper, el );
    } );
  } );
};

const changeToFirstSlide = ( sliderObj, settings ) => {
  if( settings.loop ) {
    sliderObj.slideToLoop( 0 );
  } else {
    sliderObj.slideTo( 0 );
  }
};

// Same threshold as video in-view so slider autoplay and video play start together
const TAB_SLIDER_IN_VIEW_THRESHOLD = 0.75;

const setupIntersectionObserver = ( el, swiper, navSwiper, settings ) => {
  const observer = new IntersectionObserver( ( entries ) => {
    entries.forEach( entry => {
      if ( entry.isIntersecting ) {
        el.parentNode.classList.add( 'tab-slider-in-view' );
        if ( settings.autoplay && !el.parentNode.classList.contains( 'autoplay-stop' ) ) {
          changeToFirstSlide( swiper, settings );
          startAutoPlay( swiper );
        }
        observer.unobserve( entry.target );
      } else {
        el.parentNode.classList.remove( 'tab-slider-in-view' );
        if ( settings.autoplay && !el.parentNode.classList.contains( 'autoplay-stop' ) ) {
          changeToFirstSlide( swiper, settings );
          stopAutoPlay( swiper );
        }
      }
    } );
  }, { threshold: TAB_SLIDER_IN_VIEW_THRESHOLD } );
  observer.observe( el );
};

const THUMB_ACTIVE_CLASS = 'swiper-slide-thumb-active';

function setThumbActiveImmediate( navSwiper, clickedSlide ) {
  if ( !navSwiper?.slides || !clickedSlide ) return;
  const index = Array.from( navSwiper.slides ).indexOf( clickedSlide );
  if ( index < 0 ) return;
  navSwiper.slides.forEach( ( slide, i ) => {
    slide.classList.toggle( THUMB_ACTIVE_CLASS, i === index );
  } );
}

// --- Vertical tab slider: play/pause videos (HTML5 + YouTube) on slide change, only when in view ---
const getSlideMedia = ( slide ) => {
  if ( !slide ) return { videos: [], iframes: [] };
  const videos = slide.querySelectorAll( '.e-vid video' );
  const iframes = slide.querySelectorAll( '.e-vid iframe[src*="youtube.com"], .e-vid iframe[src*="youtu.be"]' );
  return { videos: Array.from( videos ), iframes: Array.from( iframes ) };
};

const ensureYoutubeJsApi = ( iframe ) => {
  let src = iframe.getAttribute( 'src' ) || '';
  if ( src.includes( 'enablejsapi=1' ) ) return false;
  src += ( src.includes( '?' ) ? '&' : '?' ) + 'enablejsapi=1';
  iframe.setAttribute( 'src', src );
  return true;
};

const YOUTUBE_ORIGIN = 'https://www.youtube.com';

// YouTube iframe API via postMessage. ERR_BLOCKED_BY_CLIENT is from ad blockers/extensions; we avoid throwing.
const sendYoutubeCommand = ( iframe, func ) => {
  try {
    if ( iframe?.contentWindow ) {
      iframe.contentWindow.postMessage( `{"event":"command","func":"${func}","args":""}`, YOUTUBE_ORIGIN );
    }
  } catch ( e ) {
    // Ignore: blocked iframe, cross-origin, or missing contentWindow
  }
};

const playMedia = ( el ) => {
  if ( el.tagName === 'VIDEO' ) {
    el.currentTime = 0;
    el.muted = true;
    el.play().catch( ( e ) => {
      if ( e?.name !== 'AbortError' ) {
        throw e;
      }
    } );
  } else if ( el.tagName === 'IFRAME' && el.src ) {
    const srcChanged = ensureYoutubeJsApi( el );
    if ( srcChanged ) {
      el.addEventListener( 'load', () => sendYoutubeCommand( el, 'playVideo' ), { once: true } );
    } else {
      sendYoutubeCommand( el, 'playVideo' );
    }
  }
};

const stopMedia = ( el ) => {
  if ( el.tagName === 'VIDEO' ) {
    el.pause();
    el.currentTime = 0;
  } else if ( el.tagName === 'IFRAME' && el.src ) {
    sendYoutubeCommand( el, 'stopVideo' );
  }
};

const setupVerticalTabSliderVideos = ( swiper ) => {
  const videoInView = { current: false };
  let lastSyncedIndex = -1;

  const syncVideosToActiveSlide = () => {
    const slides = swiper.slides || [];
    const activeIndex = swiper.activeIndex;

    if ( !videoInView.current ) {
      lastSyncedIndex = -1;
      slides.forEach( ( slide ) => {
        const { videos, iframes } = getSlideMedia( slide );
        [ ...videos, ...iframes ].forEach( stopMedia );
      } );
      return;
    }

    if ( lastSyncedIndex === activeIndex ) {
      return;
    }
    lastSyncedIndex = activeIndex;

    slides.forEach( ( slide, index ) => {
      const { videos, iframes } = getSlideMedia( slide );
      const all = [ ...videos, ...iframes ];
      if ( index === activeIndex ) {
        all.forEach( playMedia );
      } else {
        all.forEach( stopMedia );
      }
    } );
  };

  const viewObserver = new IntersectionObserver( ( entries ) => {
    entries.forEach( ( entry ) => {
      videoInView.current = entry.isIntersecting;
      if ( !entry.isIntersecting ) {
        lastSyncedIndex = -1;
      }
      syncVideosToActiveSlide();
    } );
  }, { threshold: TAB_SLIDER_IN_VIEW_THRESHOLD } );

  swiper.on( 'afterInit', () => {
    viewObserver.observe( swiper.el );
    lastSyncedIndex = -1;
    syncVideosToActiveSlide();
  } );

  swiper.on( 'slideChangeTransitionEnd', () => {
    syncVideosToActiveSlide();
  } );
};

function setupTabSliderContentBasics( settings, el, swiper, navSwiper ) {
  const animateDuration = settings.autoplay ? settings.autoplay.delay : 0;
  setAnimationDuration( navSwiper, animateDuration );
  stopAutoPlay( swiper );
  swiper.slideTo( 0 );
  if ( settings.autoplay ) {
    el.parentNode.classList.add( 'swiper-autoplay' );
  }
}

function setupSlidingBackgroundAndTransitions( swiper, navSwiper ) {
  swiper.on( 'afterInit', () => {
    createSlidingBackground( navSwiper );
    swiper.on( 'slideChange', () => moveSlidingBackground( navSwiper, swiper.activeIndex ) );
    swiper.on( 'touchMove', () => moveSlidingBackground( navSwiper, swiper.activeIndex ) );
    swiper.on( 'transitionEnd', () => moveSlidingBackground( navSwiper, swiper.activeIndex ) );
    swiper.on( 'sliderMove', () => moveSlidingBackground( navSwiper, swiper.activeIndex ) );
    swiper.on( 'setTranslate', () => moveSlidingBackground( navSwiper, swiper.activeIndex ) );
  } );
  swiper.on( 'slideNextTransitionStart', () => {
    handleNavSlideColorTransition( navSwiper, 'next', swiper );
    handleSlideImageTransition( swiper, 'next' );
  } );
  swiper.on( 'slidePrevTransitionStart', () => {
    handleNavSlideColorTransition( navSwiper, 'prev', swiper );
    handleSlideImageTransition( swiper, 'prev' );
  } );
}

function setupTabSliderNavClickAndObserver( settings, el, swiper, navSwiper ) {
  const onNavClick = ( evt ) => {
    const slide = evt.target?.closest?.( '.swiper-slide' );
    if ( !slide || !navSwiper.slides?.length ) return;
    const index = Array.from( navSwiper.slides ).indexOf( slide );
    if ( index < 0 ) return;
    setThumbActiveImmediate( navSwiper, slide );
    if ( navSwiper.el.querySelector( '.slide-transition-bg' ) ) {
      moveSlidingBackground( navSwiper, index );
      setTimeout( () => moveSlidingBackground( navSwiper, swiper.activeIndex ), 100 );
    }
    if ( settings.loop ) {
      swiper.slideToLoop( index );
    } else {
      swiper.slideTo( index );
    }
    handleInteraction( swiper, navSwiper, el );
  };
  navSwiper.el.addEventListener( 'click', onNavClick );
  swiper.on( 'touchEnd', () => handleInteraction( swiper, navSwiper, el ) );
  setupIntersectionObserver( el, swiper, navSwiper, settings );
  swiper.on( 'afterInit', ( contentSwiper ) => {
    const dots = contentSwiper.el.querySelectorAll( '.swiper-pagination-bullet' );
    handleDotsInteraction( contentSwiper, navSwiper, el, dots );
  } );
}

const initSliderContent = ( settings, el, swiper ) => {
  configureSwiper( settings, swiper );
  const navSwiper = swiper.thumbs.swiper;
  if ( !swiper.el.closest( '.e-tbs .e-tbs__content' ) ) return;

  setupTabSliderContentBasics( settings, el, swiper, navSwiper );

  const isColorThemeOrAccordion = swiper.el.closest( '.e-stn--tabs-slider-with-color-theme-tabs .e-tbs__content, .e-stn--tab-slider-with-accordion .e-tbs__content' );
  if ( isColorThemeOrAccordion ) {
    setupSlidingBackgroundAndTransitions( swiper, navSwiper );
  }
  if ( swiper.el.closest( '.e-stn--vertical-tab-slider' ) ) {
    setupVerticalTabSliderVideos( swiper );
  }
  setupTabSliderNavClickAndObserver( settings, el, swiper, navSwiper );
};

addAction( 'b3rg_tab_slider_content_init', 'b3rg/tab-slider', initSliderContent );

addAction( 'b3rg_tab_slider_nav_init', 'b3rg/tab-slider', ( settings, el, swiper ) => {
  configureSwiper( settings, swiper );
} );
